package com.banyan.flightviewer.state

import com.banyan.flightviewer.util.nowMs
import kotlinx.serialization.Serializable

@Serializable
data class AircraftData(
    val hex: String = "Unknown",
    val callsign: String = "Unknown",
    val lat: Double = 0.0,
    val lon: Double = 0.0,
    val altitude: String = "0",       // Can be numeric or "ground"
    val speed: Double = 0.0,
    val heading: Double = 0.0,
    val verticalRate: Int = 0,
    val distance: Double = 0.0,       // km
    val aircraftType: String = "Unknown",
    val squawk: String = "",
    val origin: String = "",
    val destination: String = "",
    val registration: String = "",
    val fr24Id: String = "",
) {
    /** Altitude as a numeric value, or 0 if "ground" or unparsable */
    val altitudeNumeric: Double
        get() = altitude.toDoubleOrNull() ?: 0.0

    /** Distance in miles */
    val distanceMiles: Double
        get() = distance * 0.621371
}

@Serializable
data class AppSettings(
    val apiSource: String = "ADS-B Exchange",
    val adsbxApiKey: String = "",
    val fr24ApiKey: String = "",
    val lat: Double = 40.7128,
    val lon: Double = -74.0060,
    val radiusKm: Int = 50,
    val updateIntervalSec: Int = 10,
    val pixooEnabled: Boolean = false,
    val pixooIpAddress: String = "",
    val pixooBuzzerEnabled: Boolean = false,
    val pixooRadiusMiles: Double = 5.0,
    val pixooDisplayMode: String = "nearest",  // "nearest" or "selected"
    val distanceUnits: String = "miles",    // "miles" or "km"
    val altitudeUnits: String = "ft",      // "ft" or "m"
    val speedUnits: String = "kts",        // "kts", "kmh", or "mph"
    val savedAddress: String = "",
    val enrichmentCooldownSec: Int = 60,
    val fr24BulkEnrichment: Boolean = true,
) {
    companion object {
        fun default() = AppSettings()
    }
}

@Serializable
data class DetectedLocation(
    val lat: Double,
    val lon: Double,
    val name: String,
)

@Serializable
data class EnrichedFlightData(
    val origin: String = "",
    val destination: String = "",
    val aircraftType: String = "",
    val registration: String = "",
    val fr24Id: String = "",
    val eta: String = "",              // ISO 8601 ETA from FR24
    val departureTime: String = "",    // ISO 8601 departure time
    val cachedAt: Long = nowMs(),  // for TTL eviction
)

@Serializable
data class Bookmark(
    val hex: String = "",
    val callsign: String = "",
    val registration: String = "",
    val aircraftType: String = "",
    val airline: String = "",
    val origin: String = "",
    val destination: String = "",
    val timestamp: Long = nowMs(),
)

/**
 * A favorited airport. Stored in settings so the user's list survives
 * restarts; tapping one in the Favorites panel jumps the map to that
 * airport and opens the AirportPanel.
 *
 * `code` is the IATA identifier (e.g. "SRQ", "LAX"). `name` is cached
 * at save-time for display purposes; the canonical lookup at render
 * time goes through [com.banyan.flightviewer.data.AirportDatabase].
 */
@Serializable
data class AirportFavorite(
    val code: String = "",
    val name: String = "",
    val timestamp: Long = nowMs(),
)

@Serializable
data class AirportInfo(
    val code: String = "",
    val icao: String = "",
    val name: String = "",
    val city: String = "",
    val country: String = "",
    val lat: Double = 0.0,
    val lon: Double = 0.0,
    val elevation: Int = 0,
    val timezone: String = "",
    val utcOffset: String = "",
)

@Serializable
data class MetarData(
    val raw: String = "",
    val temp: Int? = null,
    val dewpoint: Int? = null,
    val windDir: Int? = null,
    val windSpeed: Int? = null,
    val windGust: Int? = null,
    val visibility: String? = null,
    val condition: String = "",
    val clouds: String = "",
    val altimeter: Double? = null,
)

@Serializable
data class AirportFlight(
    val callsign: String = "",
    val flightNumber: String = "",
    val origin: String = "",
    val destination: String = "",
    val aircraftType: String = "",
    val registration: String = "",
    val airline: String = "",
    val scheduledTime: String = "",
    val actualTime: String = "",
    val status: String = "", // "en route", "landed", "scheduled"
    val lat: Double = 0.0,
    val lon: Double = 0.0,
)

@Serializable
data class FlightTrackPoint(
    val lat: Double,
    val lon: Double,
    val altitude: Double,
    val timestamp: Long,
    val speed: Double = 0.0,
    val heading: Double = 0.0,
)
